import { createI18n } from "vue-i18n";
import { runtimeConfig } from "./runtime-config";

export type AppLocale = "zh-CN" | "en-US";

export const appLocale: AppLocale = runtimeConfig.market.isChinaVersion ? "zh-CN" : "en-US";

export const messages = {
  "zh-CN": {
    common: {
      brand: "Autumn Pet",
      console: "写实桌宠生成台",
      confirm: "确认",
      cancel: "取消",
      close: "关闭",
      loading: "处理中",
      unused: "先不用",
      creditsUnit: "次额度",
      petUnit: "只宠物"
    },
    footer: {
      copyrightPrefix: "Copyright © 2026",
      copyrightBrand: "Autumn Soft",
      copyrightSuffix: "All rights reserved.",
      icp: "闽ICP备2024080396号-3",
      legalNav: "法律信息",
      privacy: "隐私政策",
      terms: "服务条款"
    },
    login: {
      visualAria: "产品宣传图",
      heroAlt: "写实猫狗桌宠宣传图",
      heroTitle: "让你的猫猫狗狗住进桌面",
      heroText: "上传一张照片，生成会坐下、散步、打招呼和休息的专属桌宠。",
      welcome: "欢迎回来",
      title: "登录后开始生成",
      firstUse: "首次使用会创建账号。",
      email: "邮箱",
      password: "密码",
      passwordPlaceholder: "请输入密码",
      loggingIn: "正在登录",
      login: "登录",
      chooseProvider: "选择登录方式",
      wechatScanTitle: "微信扫码登录",
      wechatScanHint: "打开微信扫一扫，确认后会自动进入生成页面。",
      wechatFrameTitle: "微信登录二维码",
      wechatLoadFailed: "微信二维码加载失败，请刷新页面后再试。",
      loginFailed: "登录失败，请稍后重试。"
    },
    auth: {
      invalidEmail: "请输入正确的邮箱地址。",
      passwordRequired: "请输入密码。",
      currentPasswordRequired: "请输入当前密码。",
      newPasswordRequired: "请输入新密码。"
    },
    loginProviders: {
      google: "Google 登录",
      wechat: "微信登录",
      loadFailed: "登录方式加载失败。"
    },
    image: {
      readUnsupported: "浏览器暂时不能读取这张图片，请换一张再试。",
      readFailed: "图片读取失败，请换一张更清晰的照片。",
      compressUnsupported: "浏览器暂时不能压缩图片，请换一张小一些的照片。",
      compressFailed: "图片压缩失败，请换一张更小的照片。",
      tooLargeAfterCompress: "图片压缩后仍超过 15MB，请换一张更小或更清晰的照片。"
    },
    generate: {
      notLoggedIn: "未登录账号",
      resetPassword: "重置密码",
      logout: "退出",
      quota: "剩余 {count} 次额度",
      petCount: "{count}/{limit} 只宠物",
      eyebrow: "Photo to Desktop Pet",
      title: "生成一套写实宠物动作视频",
      currentPet: "当前宠物",
      choosePet: "选择宠物",
      synced: "已同步",
      addPet: "增加宠物",
      photoTitle: "宠物照片",
      photoHint: "建议单只宠物、四肢清晰、少遮挡。",
      choosePhoto: "选择或拖入照片",
      preparingPhoto: "正在给照片做准备",
      viewSource: "点击查看原图",
      sourceAlt: "上传的宠物预览图",
      infoTitle: "宠物信息",
      existingInfoHint: "当前沿用已有宠物的信息。",
      newInfoHint: "后续对话会使用这里的名字和称呼。",
      petName: "宠物名字",
      petNamePlaceholder: "例如 Milo",
      userAlias: "对你的称呼",
      userAliasPlaceholder: "例如 主人、姐姐、爸爸",
      actionTitle: "选择动作",
      actionHint: "坐姿和散步默认生成，额外动作会多花一点制作额度。",
      estimatedCost: "预计 {count} 次额度",
      actionCost: "{count} 次额度",
      defaultAction: "默认",
      advancedTitle: "高级动作描述",
      advancedHint: "可选，增加高级动作描述会额外扣除 1 次额度。",
      advancedPlaceholder: "例如：打招呼时抬右爪，坐姿只做轻微喘气。不要写太复杂。",
      progressTitle: "生成进度",
      currentActions: "当前动作：{actions}",
      noActions: "未选择",
      compressing: "处理照片中",
      generating: "生成中",
      generate: "生成",
      pairingTitle: "桌宠配对码",
      pairingHint: "已安装桌宠端可一键打开同步；也可手动打开桌宠输入当前配对码。重新同步会更新配对码。",
      directSync: "直接同步",
      downloadClient: "下载客户端",
      reviewTitle: "视频验收",
      reviewHint: "点击视频可以放大观看，每个动作有 1 次免费重新生成机会。",
      syncing: "同步中",
      syncDesktop: "同步桌宠",
      waitingVideo: "等待视频",
      regenerateAgain: "再次重新生成",
      regenerate: "重新生成",
      wechatQrAlt: "微信支付二维码",
      wechatQrHint: "请用微信扫一扫完成支付，到账后会自动确认。",
      pendingOrder: "待支付订单",
      refreshPaymentLoading: "确认中",
      refreshPayment: "我已支付，检查到账",
      useCreditTitle: "消耗 1 次额度继续",
      useCreditHint: "会消耗 1 次额度，只重新准备这一个动作。",
      remainingCredits: "剩余 {count} 次",
      paymentLoading: "正在确认订单...",
      noProducts: "暂时没有可购买的商品。",
      buyCredits: "购买额度",
      resetTitle: "重置密码",
      resetHint: "为了保护你的宠物资料，请先输入当前密码。",
      currentPassword: "当前密码",
      newPassword: "新密码",
      confirmPassword: "确认新密码",
      resetting: "重置中",
      confirmReset: "确认重置",
      status: {
        idle: "待生成",
        queued: "排队中",
        generating: "生成中",
        complete: "已完成",
        error: "失败"
      },
      builtInActions: {
        idle: "坐姿",
        walk: "散步",
        happy: "打招呼",
        eat: "喂食",
        sleep: "休息"
      },
      directSyncNoCode: "还没有可用的配对码，请先同步桌宠。重新同步会更新配对码。",
      directSyncStarted: "已尝试打开本地桌宠。如果没有弹出确认框，请先安装或启动桌宠，再手动输入当前配对码。",
      downloadWindowsOnly: "目前桌宠客户端只支持 Windows 版本，Mac OS 版本还在准备中。",
      resetMismatch: "两次输入的新密码不一致。",
      resetSuccess: "密码已经重置成功。",
      resetFailed: "重置密码失败，请稍后再试。"
    },
    store: {
      initialProgress: "上传照片后，Milo 的动作会在这里慢慢准备好。",
      buyCreditsTitle: "购买生成额度",
      buyCreditsMessage: "当前支持{provider}，到账后新的制作额度会立即回到账号里。",
      buyPetSlotTitle: "购买宠物位",
      buyPetSlotMessage: "当前支持{provider}。购买宠物位后，可以继续创建新的桌宠。",
      regenerateTitle: "需要消耗 1 次额度",
      regenerateMessage: "这个动作已经免费重试过一次啦。可以消耗 1 次额度继续重新生成。",
      productsLoadFailed: "暂时获取不到商品列表。",
      openPaymentFailed: "暂时无法打开支付页面，请稍后再试。",
      openingStripe: "正在打开 Stripe 支付页面。",
      orderWaiting: "订单已创建，等待{provider}确认到账。",
      rightsArrived: "权益已经到账。",
      paymentFailed: "支付处理失败，请稍后再试。",
      noPendingOrder: "还没有待确认的订单。",
      orderStillWaiting: "订单还在等待{provider}确认，稍后可以再刷新一次。",
      refreshPaymentFailed: "刷新支付状态失败，请稍后再试。",
      unsupportedPhoto: "只支持 PNG、JPG、WEBP 图片。",
      photoReadyToCheck: "照片收好啦，点击生成后会先检查大小和分辨率。",
      petNotFound: "找不到这只宠物，请刷新页面后重试。",
      continuePet: "继续给 {petName} 准备新动作，{userAlias}。",
      newPetReady: "可以开始准备一只新的桌宠啦。",
      chooseExistingPet: "请选择要继续生成动作的宠物。",
      uploadPhotoFirst: "请先上传一张宠物照片。",
      petNameRequired: "请填写宠物名字。",
      userAliasRequired: "请填写宠物对你的称呼。",
      customTooLong: "高级动作描述不能超过 120 个字符。",
      customRejected: "高级动作描述请保持温和、简单、写实，不能包含伤害、血腥、异常肢体、恐怖化或过复杂动作。",
      checkingPhoto: "先检查 {petName} 的照片大小和分辨率，{userAlias}稍等一下。",
      jobFailed: "生成失败，请稍后重试。",
      actionFailed: "动作没有准备成功，请稍后再试。",
      actionsReady: "动作都准备好啦，{userAlias}可以先看看 {petName} 的小视频。",
      checkingPhotoWarm: "正在轻轻检查照片。",
      photoCompressed: "照片已经收拾到 {size}，马上给 {petName} 做准备。",
      photoGood: "照片状态很好，正在给宠物准备干净的形象。",
      photoPrepared: "{petName} 的照片已经准备好，开始安排动作。",
      chooseRegenerate: "请先选择要重新生成的动作。",
      submitFirst: "请先提交一次生成任务。",
      rehearsingAgain: "{action}正在重新排练，稍等一下。",
      actionNoVideo: "动作已经完成，但暂时没有看到视频，请稍后再试。",
      regenerateReady: "{label}重新生成好了，{userAlias}可以再看一眼。",
      regenerateFailed: "重新生成失败，请稍后重试。",
      syncNeedVideos: "请先生成并验收已选择的动作视频。",
      syncNeedJob: "请先提交一次生成任务。",
      changingSkin: "正在给 {petName} 换上新的小衣服。",
      packageFailed: "桌宠皮肤包整理失败。",
      syncFailed: "同步失败，请稍后重试。",
      noPetToSync: "还没有可同步的宠物。",
      pairingFailed: "生成桌宠配对码失败，请稍后再试。",
      paymentProviderSetup: "暂时无法打开支付页面，请稍后再试。",
      unauthorized: "登录已失效，请重新登录后继续。",
      network: "暂时连接不上服务，请检查网络或稍后再试。",
      paidButQuotaMissing: "支付已经确认，但权益状态暂时没有刷新出来，请稍后刷新订单。",
      warmDefault: "{petName} 正在认真排练动作，{userAlias}稍等一下。",
      warmFailed: "{petName} 这次动作没有准备好，我们稍后再试一次。",
      warmVideo: "{petName} 的小视频正在路上，{userAlias}稍等一下。",
      warmPackage: "正在给 {petName} 整理桌面上的小窝，{userAlias}稍等一下。",
      pairingCopied: "{petName} 的新皮肤已经打包好啦，当前配对码也放进剪贴板了，{userAlias}打开桌宠就能接它回桌面。",
      pairingReady: "{petName} 的新皮肤已经打包好啦，{userAlias}打开桌宠输入当前配对码，就能接它回桌面。",
      fallbackPetName: "宠物",
      fallbackAlias: "你",
      fallbackAction: "这个动作"
    }
  },
  "en-US": {
    common: {
      brand: "Autumn Pet",
      console: "Realistic Desktop Pet Studio",
      confirm: "Confirm",
      cancel: "Cancel",
      close: "Close",
      loading: "Working",
      unused: "Maybe later",
      creditsUnit: "credits",
      petUnit: "pets"
    },
    footer: {
      copyrightPrefix: "Copyright © 2026",
      copyrightBrand: "Autumn Soft",
      copyrightSuffix: "All rights reserved.",
      icp: "Min ICP No. 2024080396-3",
      legalNav: "Legal information",
      privacy: "Privacy Policy",
      terms: "Terms of Service"
    },
    login: {
      visualAria: "Product visual",
      heroAlt: "Realistic cat and dog desktop pet preview",
      heroTitle: "Bring your pet onto your desktop",
      heroText: "Upload one photo and create a personal desktop pet that can sit, walk, wave, and rest.",
      welcome: "Welcome back",
      title: "Sign in to start",
      firstUse: "Your account will be created on first use.",
      email: "Email",
      password: "Password",
      passwordPlaceholder: "Enter your password",
      loggingIn: "Signing in",
      login: "Sign in",
      chooseProvider: "Choose a sign-in method",
      wechatScanTitle: "Scan with WeChat",
      wechatScanHint: "Use WeChat to scan the code. You will enter the workspace after confirmation.",
      wechatFrameTitle: "WeChat sign-in QR code",
      wechatLoadFailed: "WeChat QR code failed to load. Please refresh and try again.",
      loginFailed: "Sign-in failed. Please try again later."
    },
    auth: {
      invalidEmail: "Enter a valid email address.",
      passwordRequired: "Enter your password.",
      currentPasswordRequired: "Enter your current password.",
      newPasswordRequired: "Enter a new password."
    },
    loginProviders: {
      google: "Sign in with Google",
      wechat: "Sign in with WeChat",
      loadFailed: "Unable to load sign-in methods."
    },
    image: {
      readUnsupported: "This browser cannot read the image. Please try another photo.",
      readFailed: "Image loading failed. Please use a clearer photo.",
      compressUnsupported: "This browser cannot compress the image. Please try a smaller photo.",
      compressFailed: "Image compression failed. Please try a smaller photo.",
      tooLargeAfterCompress: "The image is still over 15MB after compression. Please use a smaller or clearer photo."
    },
    generate: {
      notLoggedIn: "Not signed in",
      resetPassword: "Reset password",
      logout: "Log out",
      quota: "{count} credits left",
      petCount: "{count}/{limit} pets",
      eyebrow: "Photo to Desktop Pet",
      title: "Create realistic pet action videos",
      currentPet: "Current pet",
      choosePet: "Choose a pet",
      synced: "Synced",
      addPet: "Add pet",
      photoTitle: "Pet photo",
      photoHint: "Use one clearly visible pet with paws and body unobstructed.",
      choosePhoto: "Choose or drop a photo",
      preparingPhoto: "Preparing the photo",
      viewSource: "View original",
      sourceAlt: "Uploaded pet preview",
      infoTitle: "Pet profile",
      existingInfoHint: "Using the saved profile for this pet.",
      newInfoHint: "Future dialogue will use this pet name and nickname for you.",
      petName: "Pet name",
      petNamePlaceholder: "e.g. Milo",
      userAlias: "What your pet calls you",
      userAliasPlaceholder: "e.g. Mom, Dad, buddy",
      actionTitle: "Choose actions",
      actionHint: "Sit and walk are included by default. Extra actions use more credits.",
      estimatedCost: "Estimated {count} credits",
      actionCost: "{count} credits",
      defaultAction: "Default",
      advancedTitle: "Advanced action description",
      advancedHint: "Optional. Advanced descriptions cost 1 extra credit.",
      advancedPlaceholder: "For example: raise the right paw when waving, keep idle motion to gentle breathing.",
      progressTitle: "Generation progress",
      currentActions: "Current actions: {actions}",
      noActions: "None selected",
      compressing: "Preparing photo",
      generating: "Generating",
      generate: "Generate",
      pairingTitle: "Desktop pairing code",
      pairingHint: "Open the installed desktop app to sync directly, or enter this pairing code manually. Re-syncing will refresh the code.",
      directSync: "Sync directly",
      downloadClient: "Download client",
      reviewTitle: "Video review",
      reviewHint: "Click a video to review it. Each action has 1 free regenerate.",
      syncing: "Syncing",
      syncDesktop: "Sync desktop pet",
      waitingVideo: "Waiting for video",
      regenerateAgain: "Regenerate again",
      regenerate: "Regenerate",
      wechatQrAlt: "WeChat Pay QR code",
      wechatQrHint: "Scan with WeChat to pay. Credits will update automatically after confirmation.",
      pendingOrder: "Pending order",
      refreshPaymentLoading: "Checking",
      refreshPayment: "I've paid, check status",
      useCreditTitle: "Use 1 credit to continue",
      useCreditHint: "Only this action will be prepared again.",
      remainingCredits: "{count} left",
      paymentLoading: "Checking the order...",
      noProducts: "No products are available right now.",
      buyCredits: "Buy credits",
      resetTitle: "Reset password",
      resetHint: "Enter your current password to protect your pet data.",
      currentPassword: "Current password",
      newPassword: "New password",
      confirmPassword: "Confirm new password",
      resetting: "Resetting",
      confirmReset: "Reset password",
      status: {
        idle: "Not started",
        queued: "Queued",
        generating: "Generating",
        complete: "Complete",
        error: "Failed"
      },
      builtInActions: {
        idle: "Sit",
        walk: "Walk",
        happy: "Wave",
        eat: "Eat / Drink",
        sleep: "Rest"
      },
      directSyncNoCode: "No pairing code yet. Sync the desktop pet first; re-syncing will refresh the code.",
      directSyncStarted: "Tried to open the desktop app. If nothing appeared, install or start Autumn Pet and enter the pairing code manually.",
      downloadWindowsOnly: "The desktop client currently supports Windows only. The Mac OS version is not available yet.",
      resetMismatch: "The new passwords do not match.",
      resetSuccess: "Password reset successfully.",
      resetFailed: "Password reset failed. Please try again later."
    },
    store: {
      initialProgress: "Upload a photo and Milo's actions will slowly get ready here.",
      buyCreditsTitle: "Buy generation credits",
      buyCreditsMessage: "{provider} is supported now. New credits will return to your account after payment.",
      buyPetSlotTitle: "Buy a pet slot",
      buyPetSlotMessage: "{provider} is supported now. After buying a pet slot, you can create another desktop pet.",
      regenerateTitle: "1 credit is needed",
      regenerateMessage: "This action has already used its free retry. Use 1 credit to regenerate it again.",
      productsLoadFailed: "Products are unavailable right now.",
      openPaymentFailed: "Unable to open the payment page. Please try again later.",
      openingStripe: "Opening Stripe Checkout.",
      orderWaiting: "Order created. Waiting for {provider} confirmation.",
      rightsArrived: "Your benefits have arrived.",
      paymentFailed: "Payment failed. Please try again later.",
      noPendingOrder: "There is no pending order to check.",
      orderStillWaiting: "The order is still waiting for {provider}. Try refreshing again soon.",
      refreshPaymentFailed: "Unable to refresh payment status. Please try again later.",
      unsupportedPhoto: "Only PNG, JPG, and WEBP images are supported.",
      photoReadyToCheck: "Photo received. Click Generate and we'll check size and resolution first.",
      petNotFound: "This pet was not found. Please refresh and try again.",
      continuePet: "Preparing new actions for {petName}, {userAlias}.",
      newPetReady: "Ready to prepare a new desktop pet.",
      chooseExistingPet: "Choose the pet you want to continue with.",
      uploadPhotoFirst: "Upload a pet photo first.",
      petNameRequired: "Enter a pet name.",
      userAliasRequired: "Enter what your pet should call you.",
      customTooLong: "Advanced descriptions must be 120 characters or fewer.",
      customRejected: "Keep advanced descriptions gentle, simple, and realistic. Avoid harm, gore, abnormal limbs, horror, or overly complex moves.",
      checkingPhoto: "Checking {petName}'s photo size and resolution. Hang tight, {userAlias}.",
      jobFailed: "Generation failed. Please try again later.",
      actionFailed: "The action was not prepared successfully. Please try again later.",
      actionsReady: "All actions are ready. {userAlias}, you can review {petName}'s videos now.",
      checkingPhotoWarm: "Gently checking the photo.",
      photoCompressed: "Photo trimmed to {size}. Preparing {petName} now.",
      photoGood: "The photo looks good. Preparing a clean pet cutout.",
      photoPrepared: "{petName}'s photo is ready. Starting the actions.",
      chooseRegenerate: "Choose an action to regenerate first.",
      submitFirst: "Submit a generation job first.",
      rehearsingAgain: "{action} is rehearsing again. One moment.",
      actionNoVideo: "The action is complete, but the video is not visible yet. Please try again later.",
      regenerateReady: "{label} is ready again. {userAlias}, take another look.",
      regenerateFailed: "Regeneration failed. Please try again later.",
      syncNeedVideos: "Generate and review the selected action videos first.",
      syncNeedJob: "Submit a generation job first.",
      changingSkin: "Dressing {petName} in the new desktop outfit.",
      packageFailed: "Desktop pet package failed.",
      syncFailed: "Sync failed. Please try again later.",
      noPetToSync: "There is no pet ready to sync.",
      pairingFailed: "Unable to create a desktop pairing code. Please try again later.",
      paymentProviderSetup: "Unable to open the payment page. Please try again later.",
      unauthorized: "Your session expired. Please sign in again.",
      network: "Unable to connect to the service. Please check your network or try again later.",
      paidButQuotaMissing: "Payment is confirmed, but benefits have not refreshed yet. Please refresh the order later.",
      warmDefault: "{petName} is carefully rehearsing. Hang tight, {userAlias}.",
      warmFailed: "{petName}'s action was not ready this time. We'll try again later.",
      warmVideo: "{petName}'s little video is on the way. Hang tight, {userAlias}.",
      warmPackage: "Preparing a cozy desktop home for {petName}. Hang tight, {userAlias}.",
      pairingCopied: "{petName}'s new package is ready, and the pairing code is copied. Open the desktop app to bring it home, {userAlias}.",
      pairingReady: "{petName}'s new package is ready. Open the desktop app and enter the pairing code to bring it home, {userAlias}.",
      fallbackPetName: "your pet",
      fallbackAlias: "friend",
      fallbackAction: "This action"
    }
  }
};

export const i18n = createI18n({
  legacy: false,
  locale: appLocale,
  fallbackLocale: "zh-CN",
  messages
});

export function t(key: string, values?: Record<string, unknown>) {
  return i18n.global.t(key, values ?? {});
}
